import { useEffect, useState, useMemo, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, RefreshCw, Trash2, Search, Mail, Phone, MapPin, Target, Users, CheckCircle2, Clock, UserPlus, Copy, Check, X, CreditCard, ExternalLink } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const PIPELINE = [
  "new_lead", "contacted", "scheduled", "evaluation_completed",
  "membership_recommended", "checkout_sent", "membership_active", "not_ready", "closed",
];
const STATUS_LABELS = {
  new_lead: "New Lead",
  contacted: "Contacted",
  scheduled: "Scheduled",
  evaluation_completed: "Eval Completed",
  membership_recommended: "Recommended",
  checkout_sent: "Checkout Sent",
  membership_active: "Member Active",
  not_ready: "Not Ready",
  closed: "Closed",
};
const STATUS_STYLES = {
  new_lead: "text-white border-white/50",
  contacted: "text-white/70 border-white/30",
  scheduled: "text-white/70 border-white/30",
  evaluation_completed: "text-white border-white/50",
  membership_recommended: "text-white border-white/60",
  checkout_sent: "text-black bg-white border-white",
  membership_active: "text-black bg-white border-white",
  not_ready: "text-white/40 border-white/20",
  closed: "text-white/40 border-white/20",
};

export default function LeadsTab() {
  const [inquiries, setInquiries] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [openInvite, setOpenInvite] = useState(null);
  const [openRec, setOpenRec] = useState(null);
  const [roleSel, setRoleSel] = useState({});
  const [planSel, setPlanSel] = useState({});
  const [noteSel, setNoteSel] = useState({});
  const [results, setResults] = useState({});
  const [copied, setCopied] = useState(null);
  const [inviting, setInviting] = useState(false);
  const [recommending, setRecommending] = useState(false);

  const ROLES = [
    ["athlete", "Athlete"],
    ["parent", "Parent / Guardian"],
    ["coach", "Coach"],
    ["org", "Organization"],
  ];

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/inquiries");
      setInquiries(data);
    } catch {
      toast.error("Failed to load leads.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/membership-plans").then(({ data }) => setPlans(data.plans || [])).catch(() => {});
  }, []);

  const sendInvite = async (id) => {
    setInviting(true);
    try {
      const { data } = await api.post(`/admin/inquiries/${id}/invite`, { role: roleSel[id] || "athlete" });
      setResults((p) => ({ ...p, [id]: { type: "invite", ...data } }));
      setInquiries((prev) => prev.map((x) => (x.id === id ? { ...x, status: "membership_active" } : x)));
      toast.success(data.emailed ? "Invite emailed — link ready to copy too" : "Invite link created (email not delivered — copy it below)");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setInviting(false);
    }
  };

  const sendRecommendation = async (id) => {
    const lookup_key = planSel[id];
    if (!lookup_key) { toast.error("Select a plan first."); return; }
    setRecommending(true);
    try {
      const { data } = await api.post(`/admin/inquiries/${id}/recommend-membership`, {
        lookup_key, note: noteSel[id] || null, origin_url: window.location.origin,
      });
      setResults((p) => ({ ...p, [id]: { type: "recommend", ...data } }));
      setInquiries((prev) => prev.map((x) => (x.id === id ? { ...x, status: "checkout_sent", checkout_url: data.checkout_url, recommended_plan_name: data.plan } : x)));
      toast.success(data.emailed ? `Checkout link emailed for ${data.plan}` : `Checkout link created — copy it below`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setRecommending(false);
    }
  };

  const copyLink = (id, link) => {
    navigator.clipboard.writeText(link);
    setCopied(id);
    setTimeout(() => setCopied(null), 1500);
  };

  const setStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/inquiries/${id}`, { status });
      setInquiries((prev) => prev.map((i) => (i.id === id ? data : i)));
      toast.success(`Marked as ${STATUS_LABELS[status] || status}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Update failed.");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/inquiries/${id}`);
      setInquiries((prev) => prev.filter((i) => i.id !== id));
      toast.success("Lead deleted.");
    } catch {
      toast.error("Delete failed.");
    }
  };

  const filtered = useMemo(() => {
    return inquiries.filter((i) => {
      const matchStatus = filter === "all" || i.status === filter;
      const q = query.toLowerCase();
      const matchQuery = !q || [i.name, i.email, i.phone, i.sport, i.location].some((v) => (v || "").toLowerCase().includes(q));
      return matchStatus && matchQuery;
    });
  }, [inquiries, filter, query]);

  const stats = useMemo(() => ({
    total: inquiries.length,
    new: inquiries.filter((i) => i.status === "new_lead").length,
    checkout: inquiries.filter((i) => i.status === "checkout_sent").length,
    active: inquiries.filter((i) => i.status === "membership_active").length,
  }), [inquiries]);

  const STAT_CARDS = [
    { label: "Total Leads", value: stats.total, icon: Users },
    { label: "New Leads", value: stats.new, icon: Clock },
    { label: "Checkout Sent", value: stats.checkout, icon: CreditCard },
    { label: "Members Active", value: stats.active, icon: CheckCircle2 },
  ];

  return (
    <div data-testid="leads-tab">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-display font-black uppercase tracking-tighter text-4xl md:text-5xl">
          Assessment Requests
        </h1>
        <button
          data-testid="leads-refresh"
          onClick={load}
          className="inline-flex items-center gap-2 border border-white/20 px-4 py-2 text-xs uppercase tracking-widest font-bold hover:bg-white hover:text-black transition-colors"
        >
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10 mb-10">
        {STAT_CARDS.map((s) => {
          const Icon = s.icon;
          return (
            <div key={s.label} className="bg-[#0A0A0A] p-6" data-testid={`leads-stat-${s.label.toLowerCase().replace(/\s/g, "-")}`}>
              <Icon size={20} className="text-white/70 mb-3" />
              <p className="font-mono-accent text-4xl font-bold">{s.value}</p>
              <p className="text-white/40 text-xs uppercase tracking-wide mt-1">{s.label}</p>
            </div>
          );
        })}
      </div>

      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
          <input
            data-testid="leads-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search name, email, sport, location..."
            className="w-full bg-[#121212] border border-white/10 focus:border-white outline-none pl-10 pr-4 py-3 text-sm transition-colors"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          {["all", ...PIPELINE].map((s) => (
            <button
              key={s}
              data-testid={`leads-filter-${s}`}
              onClick={() => setFilter(s)}
              className={`px-3 py-2 text-[11px] uppercase tracking-widest font-bold border transition-colors ${
                filter === s ? "bg-white border-white text-black" : "border-white/20 text-white/60 hover:text-white"
              }`}
            >
              {s === "all" ? "All" : STATUS_LABELS[s]}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24 text-white/40">
          <Loader2 className="animate-spin mr-2" /> Loading...
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-24 text-white/40 border border-white/10 bg-[#121212]" data-testid="leads-empty">
          No leads found.
        </div>
      ) : (
        <div className="space-y-4" data-testid="leads-list">
          {filtered.map((i) => (
            <div key={i.id} data-testid={`lead-${i.id}`} className="bg-[#121212] border border-white/10 hover:border-white/25 transition-colors p-6">
              <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3 flex-wrap">
                    <h3 className="font-display font-bold uppercase tracking-tight text-2xl">{i.name}</h3>
                    <span
                      data-testid={`lead-status-badge-${i.id}`}
                      className={`font-mono-accent text-[10px] tracking-[0.2em] uppercase border px-2 py-1 ${STATUS_STYLES[i.status] || STATUS_STYLES.closed}`}
                    >
                      {STATUS_LABELS[i.status] || i.status}
                    </span>
                    {i.recommended_plan_name && (
                      <span className="font-mono-accent text-[10px] tracking-[0.15em] uppercase border border-white/20 text-white/50 px-2 py-1">
                        {i.recommended_plan_name}
                      </span>
                    )}
                  </div>
                  <div className="grid sm:grid-cols-2 gap-x-8 gap-y-2 text-sm text-white/60">
                    <span className="flex items-center gap-2"><Mail size={14} className="text-white/70" /> {i.email}</span>
                    <span className="flex items-center gap-2"><Phone size={14} className="text-white/70" /> {i.phone}</span>
                    {i.sport && <span className="flex items-center gap-2"><Target size={14} className="text-white/70" /> {i.sport}{i.athlete_age ? ` · Age ${i.athlete_age}` : ""}</span>}
                    {i.location && <span className="flex items-center gap-2"><MapPin size={14} className="text-white/70" /> {i.location}</span>}
                  </div>
                  {i.goal && <p className="mt-3 text-sm text-white/70"><span className="text-white/40">Goal:</span> {i.goal}</p>}
                  {i.message && <p className="mt-1 text-sm text-white/70"><span className="text-white/40">Message:</span> {i.message}</p>}
                  <p className="mt-3 font-mono-accent text-[10px] tracking-wider uppercase text-white/30">
                    {new Date(i.created_at).toLocaleString()}
                  </p>
                </div>
                <div className="flex flex-col gap-3 lg:items-end lg:min-w-[240px]">
                  <div className="w-full lg:w-auto">
                    <label className="font-mono-accent text-[10px] tracking-[0.2em] uppercase text-white/40 block mb-1.5">Pipeline Stage</label>
                    <select
                      data-testid={`lead-status-select-${i.id}`}
                      value={PIPELINE.includes(i.status) ? i.status : "new_lead"}
                      onChange={(e) => setStatus(i.id, e.target.value)}
                      className="w-full lg:w-52 bg-[#0A0A0A] border border-white/15 text-sm px-3 py-2.5 outline-none focus:border-white transition-colors"
                    >
                      {PIPELINE.map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
                    </select>
                  </div>
                  <div className="flex gap-4 flex-wrap justify-end">
                    <button
                      data-testid={`lead-recommend-${i.id}`}
                      onClick={() => { setOpenRec(openRec === i.id ? null : i.id); setOpenInvite(null); }}
                      className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-white hover:text-white/70 transition-colors"
                    >
                      {openRec === i.id ? <X size={14} /> : <CreditCard size={14} />} {openRec === i.id ? "Close" : "Recommend Membership"}
                    </button>
                    <button
                      data-testid={`lead-invite-${i.id}`}
                      onClick={() => { setOpenInvite(openInvite === i.id ? null : i.id); setOpenRec(null); }}
                      className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-white/70 hover:text-white transition-colors"
                    >
                      {openInvite === i.id ? <X size={14} /> : <UserPlus size={14} />} {openInvite === i.id ? "Close" : "Invite / Activate"}
                    </button>
                    <button
                      data-testid={`lead-delete-${i.id}`}
                      onClick={() => remove(i.id)}
                      className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-white/40 hover:text-white/70 transition-colors"
                    >
                      <Trash2 size={14} /> Delete
                    </button>
                  </div>
                </div>
              </div>

              {openRec === i.id && (
                <div className="mt-6 pt-6 border-t border-white/10" data-testid={`recommend-panel-${i.id}`}>
                  <p className="font-mono-accent text-[10px] tracking-[0.2em] uppercase text-white/40 mb-3">
                    Recommend a Membership & Send Checkout Link
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
                    <div className="flex-1 max-w-xs">
                      <label className="font-mono-accent text-[10px] tracking-[0.2em] uppercase text-white/40 block mb-1.5">Plan</label>
                      <select
                        data-testid={`recommend-plan-${i.id}`}
                        value={planSel[i.id] || ""}
                        onChange={(e) => setPlanSel((p) => ({ ...p, [i.id]: e.target.value }))}
                        className="w-full bg-[#0A0A0A] border border-white/15 text-sm px-3 py-2.5 outline-none focus:border-white transition-colors"
                      >
                        <option value="">Select a plan…</option>
                        {plans.map((p) => (
                          <option key={p.lookup_key} value={p.lookup_key}>{p.name} — ${p.price}/mo</option>
                        ))}
                      </select>
                    </div>
                    <button
                      data-testid={`recommend-send-${i.id}`}
                      onClick={() => sendRecommendation(i.id)}
                      disabled={recommending || !i.email || !planSel[i.id]}
                      className="inline-flex items-center justify-center gap-2 bg-white text-black font-bold uppercase tracking-widest text-xs px-6 py-3 hover:bg-white/85 transition-colors disabled:opacity-60"
                    >
                      {recommending ? <Loader2 size={14} className="animate-spin" /> : <CreditCard size={14} />} Generate & Send
                    </button>
                  </div>
                  <div className="mt-3 max-w-xl">
                    <label className="font-mono-accent text-[10px] tracking-[0.2em] uppercase text-white/40 block mb-1.5">Note (optional)</label>
                    <textarea
                      data-testid={`recommend-note-${i.id}`}
                      value={noteSel[i.id] || ""}
                      onChange={(e) => setNoteSel((p) => ({ ...p, [i.id]: e.target.value }))}
                      rows={2}
                      placeholder="Personal note included in the email…"
                      className="w-full bg-[#0A0A0A] border border-white/15 text-sm px-3 py-2.5 outline-none focus:border-white transition-colors"
                    />
                  </div>
                  {!i.email && <p className="text-xs text-white/40 mt-2">This lead has no email — cannot send a checkout link.</p>}

                  {results[i.id]?.type === "recommend" && (
                    <div className="mt-4 bg-[#0A0A0A] border border-white/15 p-4" data-testid={`recommend-result-${i.id}`}>
                      <p className="text-xs text-white/50 mb-2">
                        {results[i.id].emailed
                          ? `Checkout link for ${results[i.id].plan} emailed to ${results[i.id].email}. You can also copy it:`
                          : `Email not delivered (verify a domain in Resend for auto-send). Copy this checkout link and send it to ${results[i.id].email}:`}
                      </p>
                      <div className="flex items-center gap-2">
                        <input
                          data-testid={`recommend-link-${i.id}`}
                          readOnly
                          value={results[i.id].checkout_url}
                          className="flex-1 bg-[#121212] border border-white/10 text-xs text-white/70 px-3 py-2 outline-none"
                        />
                        <button
                          data-testid={`recommend-copy-${i.id}`}
                          onClick={() => copyLink(i.id, results[i.id].checkout_url)}
                          className="inline-flex items-center gap-2 border border-white/20 px-4 py-2 text-xs uppercase tracking-widest font-bold hover:bg-white hover:text-black transition-colors"
                        >
                          {copied === i.id ? <Check size={14} /> : <Copy size={14} />} {copied === i.id ? "Copied" : "Copy"}
                        </button>
                      </div>
                    </div>
                  )}
                  {!results[i.id] && i.checkout_url && (
                    <a
                      data-testid={`recommend-existing-link-${i.id}`}
                      href={i.checkout_url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 mt-4 text-xs uppercase tracking-widest font-bold text-white/60 hover:text-white transition-colors"
                    >
                      <ExternalLink size={14} /> Open existing checkout link
                    </a>
                  )}
                </div>
              )}

              {openInvite === i.id && (
                <div className="mt-6 pt-6 border-t border-white/10" data-testid={`invite-panel-${i.id}`}>
                  <p className="font-mono-accent text-[10px] tracking-[0.2em] uppercase text-white/40 mb-3">
                    Create Free Account & Send Activation Link
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
                    <div className="flex-1 max-w-xs">
                      <label className="font-mono-accent text-[10px] tracking-[0.2em] uppercase text-white/40 block mb-1.5">Role</label>
                      <select
                        data-testid={`invite-role-${i.id}`}
                        value={roleSel[i.id] || "athlete"}
                        onChange={(e) => setRoleSel((p) => ({ ...p, [i.id]: e.target.value }))}
                        className="w-full bg-[#0A0A0A] border border-white/15 text-sm px-3 py-2.5 outline-none focus:border-white transition-colors"
                      >
                        {ROLES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                      </select>
                    </div>
                    <button
                      data-testid={`invite-send-${i.id}`}
                      onClick={() => sendInvite(i.id)}
                      disabled={inviting || !i.email}
                      className="inline-flex items-center justify-center gap-2 bg-white text-black font-bold uppercase tracking-widest text-xs px-6 py-3 hover:bg-white/85 transition-colors disabled:opacity-60"
                    >
                      {inviting ? <Loader2 size={14} className="animate-spin" /> : <UserPlus size={14} />} Send Invite
                    </button>
                  </div>
                  {!i.email && <p className="text-xs text-white/40 mt-2">This lead has no email — cannot create an account.</p>}

                  {results[i.id]?.type === "invite" && (
                    <div className="mt-4 bg-[#0A0A0A] border border-white/15 p-4" data-testid={`invite-result-${i.id}`}>
                      <p className="text-xs text-white/50 mb-2">
                        {results[i.id].emailed
                          ? `Activation email sent to ${results[i.id].email}. You can also copy the link:`
                          : `Email not delivered (verify a domain in Resend for auto-send). Copy this activation link and send it to ${results[i.id].email}:`}
                      </p>
                      <div className="flex items-center gap-2">
                        <input
                          data-testid={`invite-link-${i.id}`}
                          readOnly
                          value={results[i.id].activation_link}
                          className="flex-1 bg-[#121212] border border-white/10 text-xs text-white/70 px-3 py-2 outline-none"
                        />
                        <button
                          data-testid={`invite-copy-${i.id}`}
                          onClick={() => copyLink(i.id, results[i.id].activation_link)}
                          className="inline-flex items-center gap-2 border border-white/20 px-4 py-2 text-xs uppercase tracking-widest font-bold hover:bg-white hover:text-black transition-colors"
                        >
                          {copied === i.id ? <Check size={14} /> : <Copy size={14} />} {copied === i.id ? "Copied" : "Copy"}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
